import {
  CreateTenantRequest,
  tenantApi,
  UpdateTenantRequest,
} from '@/api/tenants';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

const FormContainer = styled.div`
  min-height: 100vh;
  background: var(--gray-50);
  padding: 2rem;
`;

const FormContent = styled.div`
  max-width: 1560px;
  margin: 0 auto;
`;

const Header = styled.header`
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-700);
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    border-color: var(--primary-600);
    color: var(--primary-600);
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: var(--gray-600);
`;

const Form = styled.form`
  background: white;
  border-radius: 0.75rem;
  box-shadow: var(--shadow-sm);
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--gray-200);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-700);

  .required {
    color: var(--error-600);
    margin-left: 0.25rem;
  }
`;

const Input = styled.input`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }

  &:disabled {
    background: var(--gray-100);
    cursor: not-allowed;
  }

  &.error {
    border-color: var(--error-600);
  }
`;

const Select = styled.select`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }

  &:disabled {
    background: var(--gray-100);
    cursor: not-allowed;
  }
`;

const HelpText = styled.p`
  font-size: 0.75rem;
  color: var(--gray-500);
  margin-top: 0.25rem;
`;

const ErrorText = styled.p`
  font-size: 0.75rem;
  color: var(--error-600);
  margin-top: 0.25rem;
`;

const SubdomainPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--gray-700);
  font-family: monospace;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  input[type='checkbox'] {
    width: 1rem;
    height: 1rem;
    cursor: pointer;
    accent-color: var(--primary-600);
  }

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--gray-700);
    cursor: pointer;
    margin: 0;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--gray-200);
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${(props) =>
    props.$variant === 'primary'
      ? `
    background: var(--primary-600);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--primary-700);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
    }
  `
      : `
    background: white;
    color: var(--gray-700);
    border: 1px solid var(--gray-300);
    
    &:hover:not(:disabled) {
      border-color: var(--gray-400);
      background: var(--gray-50);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;

  &::after {
    content: '';
    width: 2rem;
    height: 2rem;
    border: 3px solid var(--gray-200);
    border-top-color: var(--primary-600);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background: var(--error-50);
  border: 1px solid var(--error-200);
  border-radius: 0.5rem;
  color: var(--error-700);
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
`;

// ── User grid styled components ──────────────────────────────────────────────
const UserTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  thead tr {
    background: var(--gray-50);
  }
  th,
  td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid var(--gray-100);
  }
  th {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--gray-500);
  }
  tbody tr:hover {
    background: var(--gray-50);
  }
`;

const RolePill = styled.span`
  display: inline-block;
  padding: 0.15rem 0.55rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--primary-100);
  color: var(--primary-700);
  margin-right: 0.25rem;
`;

const ActiveDot = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${(p) =>
    p.$active ? 'var(--success-600, #16a34a)' : 'var(--error-600, #dc2626)'};
  svg {
    width: 0.9rem;
    height: 0.9rem;
  }
`;

const EmptyUsers = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--gray-400);
  background: var(--gray-50);
  border-radius: 0.5rem;
  font-size: 0.875rem;
`;

const TenantForm: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subdomainError, setSubdomainError] = useState<string | null>(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<
    {
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      roles: string[];
      is_active: boolean;
      is_verified: boolean;
      last_login_at: string | null;
      created_at: string;
    }[]
  >([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    domain: '',
    subscription_tier: 'starter' as
      | 'starter'
      | 'professional'
      | 'enterprise'
      | 'custom',
    subscription_status: 'trial' as
      | 'trial'
      | 'active'
      | 'suspended'
      | 'cancelled',
    is_active: true,
    trial_days: 14,
  });

  // Load tenant data for editing
  useEffect(() => {
    if (isEditMode && id) {
      loadTenant(id);
      loadTenantUsers(id);
    }
  }, [id, isEditMode]);

  const loadTenant = async (tenantId: string) => {
    setLoading(true);
    setError(null);
    try {
      const tenant = await tenantApi.getTenant(tenantId);
      setFormData({
        name: tenant.company_name,
        subdomain: tenant.subdomain,
        domain: tenant.domain || '',
        subscription_tier: tenant.subscription_tier,
        subscription_status: tenant.subscription_status,
        is_active: tenant.is_active,
        trial_days: 14,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantUsers = async (tenantId: string) => {
    setUsersLoading(true);
    try {
      const users = await tenantApi.getTenantUsers(tenantId);
      setTenantUsers(users);
    } catch {
      // non-critical — silently ignore
    } finally {
      setUsersLoading(false);
    }
  };

  // Check subdomain availability
  useEffect(() => {
    if (!isEditMode && formData.subdomain.length >= 3) {
      const timer = setTimeout(() => {
        checkSubdomainAvailability(formData.subdomain);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.subdomain, isEditMode]);

  const checkSubdomainAvailability = async (subdomain: string) => {
    setCheckingSubdomain(true);
    setSubdomainError(null);
    try {
      const result = await tenantApi.checkSubdomain(subdomain);
      if (!result.available) {
        setSubdomainError(result.reason || 'Subdomain is not available');
      }
    } catch (err) {
      // Ignore errors for subdomain check
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.subdomain.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (!isEditMode && subdomainError) {
      setError('Please fix subdomain errors before submitting');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (isEditMode && id) {
        const updateData: UpdateTenantRequest = {
          name: formData.name,
          domain: formData.domain || undefined,
          is_active: formData.is_active,
          subscription_tier: formData.subscription_tier,
          subscription_status: formData.subscription_status,
        };
        await tenantApi.updateTenant(id, updateData);
      } else {
        const createData: CreateTenantRequest = {
          name: formData.name,
          subdomain: formData.subdomain,
          domain: formData.domain || undefined,
          subscription_tier: formData.subscription_tier,
          subscription_status: formData.subscription_status,
          trial_days:
            formData.subscription_status === 'trial'
              ? formData.trial_days
              : undefined,
        };
        await tenantApi.createTenant(createData);
      }

      navigate('/tenants');
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          `Failed to ${isEditMode ? 'update' : 'create'} tenant`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <FormContainer>
        <FormContent>
          <LoadingSpinner />
        </FormContent>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormContent>
        <Header>
          <BackButton onClick={() => navigate('/tenants')}>
            <ArrowLeftIcon />
            Back to Tenants
          </BackButton>
          <Title>{isEditMode ? 'Edit Tenant' : 'Create New Tenant'}</Title>
          <Subtitle>
            {isEditMode
              ? 'Update tenant information and settings'
              : 'Add a new tenant to the platform'}
          </Subtitle>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Section>
            <SectionTitle>Basic Information</SectionTitle>
            <FormGrid>
              <FormGroup>
                <Label>
                  Company Name<span className="required">*</span>
                </Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Acme Corporation"
                  required
                />
                <HelpText>The legal company name of the tenant</HelpText>
              </FormGroup>

              <FormGroup>
                <Label>
                  Subdomain<span className="required">*</span>
                </Label>
                <Input
                  type="text"
                  value={formData.subdomain}
                  onChange={(e) =>
                    handleChange(
                      'subdomain',
                      e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                    )
                  }
                  placeholder="acme"
                  disabled={isEditMode}
                  className={subdomainError ? 'error' : ''}
                  required
                />
                <SubdomainPreview>
                  {formData.subdomain || 'subdomain'}.marketeer.com
                </SubdomainPreview>
                {checkingSubdomain && (
                  <HelpText>Checking availability...</HelpText>
                )}
                {subdomainError && <ErrorText>{subdomainError}</ErrorText>}
                {!isEditMode &&
                  !subdomainError &&
                  formData.subdomain.length >= 3 &&
                  !checkingSubdomain && (
                    <HelpText style={{ color: 'var(--success-600)' }}>
                      ✓ Subdomain is available
                    </HelpText>
                  )}
              </FormGroup>

              <FormGroup>
                <Label>Custom Domain (Optional)</Label>
                <Input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => handleChange('domain', e.target.value)}
                  placeholder="shop.acmecorp.com"
                />
                <HelpText>Optional custom domain for this tenant</HelpText>
              </FormGroup>
            </FormGrid>
          </Section>

          <Section>
            <SectionTitle>Subscription Settings</SectionTitle>
            <FormGrid>
              <FormGroup>
                <Label>Subscription Tier</Label>
                <Select
                  value={formData.subscription_tier}
                  onChange={(e) =>
                    handleChange('subscription_tier', e.target.value)
                  }
                >
                  <option value="starter">Starter</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                  <option value="custom">Custom</option>
                </Select>
                <HelpText>The subscription plan for this tenant</HelpText>
              </FormGroup>

              <FormGroup>
                <Label>Subscription Status</Label>
                <Select
                  value={formData.subscription_status}
                  onChange={(e) =>
                    handleChange('subscription_status', e.target.value)
                  }
                >
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
                <HelpText>Current status of the subscription</HelpText>
              </FormGroup>

              {!isEditMode && formData.subscription_status === 'trial' && (
                <FormGroup>
                  <Label>Trial Duration (Days)</Label>
                  <Input
                    type="number"
                    value={formData.trial_days}
                    onChange={(e) =>
                      handleChange('trial_days', parseInt(e.target.value) || 14)
                    }
                    min="1"
                    max="90"
                  />
                  <HelpText>Number of days for the trial period</HelpText>
                </FormGroup>
              )}
            </FormGrid>
          </Section>

          <Section>
            <SectionTitle>Status</SectionTitle>
            <CheckboxGroup>
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
              <label htmlFor="is_active">Tenant is active</label>
            </CheckboxGroup>
            <HelpText style={{ marginTop: '0.5rem' }}>
              Inactive tenants cannot access the platform
            </HelpText>
          </Section>

          {/* Users section — edit mode only */}
          {isEditMode && (
            <Section>
              <SectionTitle>
                Users&nbsp;
                {!usersLoading && (
                  <span
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      color: 'var(--gray-500)',
                    }}
                  >
                    ({tenantUsers.length})
                  </span>
                )}
              </SectionTitle>

              {usersLoading ? (
                <div
                  style={{
                    padding: '1.5rem',
                    textAlign: 'center',
                    color: 'var(--gray-400)',
                    fontSize: '0.875rem',
                  }}
                >
                  Loading users…
                </div>
              ) : tenantUsers.length === 0 ? (
                <EmptyUsers>No users found for this tenant.</EmptyUsers>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <UserTable>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Roles</th>
                        <th>Status</th>
                        <th>Verified</th>
                        <th>Last Login</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenantUsers.map((u) => {
                        const fullName =
                          [u.first_name, u.last_name]
                            .filter(Boolean)
                            .join(' ') || '—';
                        return (
                          <tr key={u.id}>
                            <td
                              style={{
                                fontWeight: 600,
                                color: 'var(--gray-900)',
                              }}
                            >
                              {fullName}
                            </td>
                            <td style={{ color: 'var(--gray-600)' }}>
                              {u.email}
                            </td>
                            <td>
                              {(u.roles ?? []).map((r) => (
                                <RolePill key={r}>
                                  {r.replace('ROLE_', '')}
                                </RolePill>
                              ))}
                            </td>
                            <td>
                              <ActiveDot $active={u.is_active}>
                                {u.is_active ? (
                                  <>
                                    <CheckCircleIcon />
                                    Active
                                  </>
                                ) : (
                                  <>
                                    <XCircleIcon />
                                    Inactive
                                  </>
                                )}
                              </ActiveDot>
                            </td>
                            <td>
                              <ActiveDot $active={u.is_verified}>
                                {u.is_verified ? 'Yes' : 'No'}
                              </ActiveDot>
                            </td>
                            <td
                              style={{
                                color: 'var(--gray-500)',
                                fontSize: '0.8125rem',
                              }}
                            >
                              {u.last_login_at
                                ? new Date(u.last_login_at).toLocaleDateString()
                                : '—'}
                            </td>
                            <td
                              style={{
                                color: 'var(--gray-500)',
                                fontSize: '0.8125rem',
                              }}
                            >
                              {new Date(u.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </UserTable>
                </div>
              )}
            </Section>
          )}

          <FormActions>
            <Button
              type="button"
              $variant="secondary"
              onClick={() => navigate('/tenants')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              $variant="primary"
              disabled={submitting || (!isEditMode && !!subdomainError)}
            >
              {submitting
                ? 'Saving...'
                : isEditMode
                  ? 'Update Tenant'
                  : 'Create Tenant'}
            </Button>
          </FormActions>
        </Form>
      </FormContent>
    </FormContainer>
  );
};

export default TenantForm;
